"use client";
import { useEffect, useRef } from "react";

export function Reveal({
  children,
  as: As = "div",
  className = "",
  variant = "fade-up",
  once = true,
  threshold = 0.15,
}: {
  children: React.ReactNode;
  as?: any;
  className?: string;
  variant?: "fade-up" | "fade-in" | "slide-right" | "stagger" | "locations" | "word-line";
  once?: boolean;
  threshold?: number;
}) {
  const ref = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof window === "undefined") return;
    const prefersReduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduce) {
      el.classList.add("is-visible");
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            el.classList.add("is-visible");
            if (once) obs.disconnect();
          } else if (!once) {
            el.classList.remove("is-visible");
          }
        }
      },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [once, threshold]);

  // Map variant -> classes / data-attrs
  const classes = ["reveal"];
  if (variant === "stagger") classes.push("reveal-stagger");
  else if (variant === "locations") classes.push("lp-locations-strip");
  else if (variant === "word-line") classes.push("word-reveal-line");
  const dataVariant = (variant === "fade-up" || variant === "fade-in" || variant === "slide-right") ? variant : undefined;

  return (
    <As ref={ref as any} className={`${classes.join(" ")} ${className}`.trim()} data-variant={dataVariant}>
      {children}
    </As>
  );
}
